import React, { useState, useEffect, useMemo } from 'react';
import { useCRM } from '../context/CRMContext';
import { db, storage } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, query,
  where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// ─── Extract Drive info from a folder URL ────────────────────────────────────
function parseFolderUrl(url) {
  if (!url) return null;
  if (url.includes('drive.google.com')) {
    const m = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
    return m ? { provider: 'google', folderId: m[1], folderUrl: url } : null;
  }
  if (url.includes('onedrive.live.com') || url.includes('sharepoint.com') || url.includes('1drv.ms')) {
    return { provider: 'microsoft', folderId: null, folderUrl: url };
  }
  return null;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const DOC_STATUS = {
  pending:    { label: 'Pending',    color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'   },
  requested:  { label: 'Requested',  color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)'   },
  uploaded:   { label: 'Uploaded',   color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)'   },
  verified:   { label: 'Verified',   color: '#10b981', bg: 'rgba(16,185,129,0.1)'   },
  rejected:   { label: 'Rejected',   color: '#ef4444', bg: 'rgba(239,68,68,0.1)'    },
};

const DOC_CATEGORIES = [
  {
    id: 'identity', label: '🪪 Identity', docs: [
      'Passport (all pages)', 'National ID Card', 'Birth Certificate',
    ],
  },
  {
    id: 'academic', label: '📚 Academic', docs: [
      'Grade 10 Marksheet', 'Grade 12 Marksheet', 'Undergraduate Degree Certificate',
      'Undergraduate Transcripts', 'Postgraduate Transcripts', 'School Leaving Certificate',
    ],
  },
  {
    id: 'english', label: '🌐 English Tests', docs: [
      'IELTS Score Card', 'TOEFL Score Report', 'PTE Score Card', 'Duolingo English Certificate',
    ],
  },
  {
    id: 'entrance', label: '📝 Entrance Tests', docs: [
      'GRE Score Report', 'GMAT Score Report', 'SAT Score Report', 'ACT Score Report',
    ],
  },
  {
    id: 'sop', label: '✍️ Essays & SOP', docs: [
      'Statement of Purpose (SOP)', 'Personal Statement', 'Research Proposal',
      'Motivation Letter',
    ],
  },
  {
    id: 'lor', label: '📩 Recommendations', docs: [
      'LOR – Academic 1', 'LOR – Academic 2', 'LOR – Professional',
    ],
  },
  {
    id: 'financial', label: '💰 Financial', docs: [
      'Bank Statement (6 months)', 'Scholarship Award Letter', 'Sponsorship Letter',
      'Income Tax Returns', 'Salary Slips (3 months)',
    ],
  },
  {
    id: 'work', label: '💼 Work Experience', docs: [
      'Resume / CV', 'Work Experience Certificate', 'Employment Letter',
      'Pay Stubs / Salary Evidence',
    ],
  },
  {
    id: 'visa', label: '✈️ Visa & Travel', docs: [
      'Visa Application Form', 'Visa Fee Receipt', 'Travel Insurance', 'Previous Visa Copy',
      'Offer Letter from University',
    ],
  },
  {
    id: 'other', label: '📁 Other', docs: ['Photograph (passport size)', 'Gap Certificate', 'Medical Certificate'],
  },
];

const ALL_DOC_NAMES = DOC_CATEGORIES.flatMap(c => c.docs);

// Maps each DOC_CATEGORY id → the OneDrive/Google Drive subfolder key
const CATEGORY_SUBFOLDER = {
  identity:  'identity',
  academic:  'academic',
  english:   'englishTests',
  entrance:  'entranceTests',
  sop:       'essaysSop',
  lor:       'recommendations',
  financial: 'financial',
  work:      'workExperience',
  visa:      'visaTravel',
  other:     'other',
};

// ─── Badge ────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = DOC_STATUS[status] || DOC_STATUS.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 700,
    }}>{s.label}</span>
  );
}

// ─── Document Row ─────────────────────────────────────────────────────────────
// subFolders: { documents: { id, url }, ... } from studentProfiles (new arch)
// driveConfig: legacy { provider, folderId, folderUrl } | null — kept for backward compat
function DocumentRow({ docItem, onUpdate, onDelete, isAdmin, currentUser, driveConfig, studentId, subFolders }) {
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [driveErr,  setDriveErr]  = useState('');
  const fileRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setDriveErr('');

    // ── Drive / OneDrive upload path ─────────────────────────────────────
    const useNewArch = studentId && subFolders?.documents;
    if (useNewArch || driveConfig) {
      try {
        // Build clean filename: document type name + original extension
        const _ext = file.name.includes('.') ? file.name.split('.').pop() : '';
        const _cleanDocName = docItem.name.replace(/[/\\?%*:|"<>]/g, '-').trim();
        const _uploadFileName = _ext ? `${_cleanDocName}.${_ext}` : _cleanDocName;

        const fd = new FormData();
        fd.append('file',       file);
        fd.append('companyId',  docItem.companyId);
        fd.append('userId',     currentUser?.id || '');
        fd.append('docName',    _uploadFileName);
        if (useNewArch) {
          // New architecture: route to documents subfolder
          fd.append('studentId',     studentId);
          fd.append('subFolderType', 'documents');
        } else {
          // Legacy: use manual folder config
          fd.append('folderId',  driveConfig.folderId || '');
          fd.append('folderUrl', driveConfig.folderUrl || '');
          fd.append('provider',  driveConfig.provider);
        }

        const resp = await fetch(`${BACKEND_URL}/api/drive/upload`, { method: 'POST', body: fd });
        const data = await resp.json();

        if (!resp.ok || data.error) throw new Error(data.error || 'Drive upload failed');

        await onUpdate(docItem.id, {
          status:          'uploaded',
          fileUrl:         data.fileUrl,
          fileId:          data.fileId,
          fileName:        _uploadFileName,
          storageProvider: data.provider,
          storagePath:     null,
          uploadedAt:      serverTimestamp(),
          uploadedBy:      currentUser?.id,
        });
      } catch (err) {
        console.error('[DocumentRow] Drive upload error:', err.message);
        setDriveErr(err.message);
      } finally {
        setUploading(false);
      }
      return;
    }

    // ── Firebase Storage fallback (no Drive folder linked) ────────────────
    const path = `documents/${docItem.companyId}/${docItem.leadId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on('state_changed',
      (snap) => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      (err) => { console.error(err); setUploading(false); },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        await onUpdate(docItem.id, {
          status: 'uploaded', fileUrl: url, fileName: file.name,
          storagePath: path, storageProvider: 'firebase',
          uploadedAt: serverTimestamp(), uploadedBy: currentUser?.id,
        });
        setUploading(false); setProgress(0);
      }
    );
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this document?')) return;
    // Only delete from Firebase Storage if that's where it was stored
    if (docItem.storagePath && docItem.storageProvider !== 'google' && docItem.storageProvider !== 'microsoft') {
      try { await deleteObject(ref(storage, docItem.storagePath)); } catch {}
    }
    await onDelete(docItem.id);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
      borderBottom: '1px solid var(--border)', transition: 'background 0.1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {/* Icon */}
      <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 }}>
        {docItem.status === 'verified' ? '✅' : docItem.status === 'rejected' ? '❌' :
          docItem.status === 'uploaded' ? '📄' : docItem.status === 'requested' ? '🔔' : '📋'}
      </span>

      {/* Name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{docItem.name}</div>
        {docItem.remark && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>⚠️ {docItem.remark}</div>}
        {docItem.fileName && (
          <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2 }}>
            {docItem.storageProvider === 'google' ? '🟢' : docItem.storageProvider === 'microsoft' ? '🔵' : '📎'}{' '}
            <a href={docItem.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#7c3aed' }}>{docItem.fileName}</a>
          </div>
        )}
        {driveErr && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 3 }}>⚠️ {driveErr}</div>}
        {uploading && (
          <div style={{ marginTop: 4 }}>
            {(studentId && subFolders?.documents) || driveConfig ? (
              <div style={{ fontSize: 10, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                Uploading to {studentId ? 'shared drive folder' : (driveConfig?.provider === 'google' ? 'Google Drive' : 'OneDrive')}…
              </div>
            ) : (
              <>
                <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: progress + '%', background: '#7c3aed', transition: 'width 0.2s' }} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>{progress}%</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Status */}
      <StatusBadge status={docItem.status} />

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {/* Upload */}
        {(docItem.status === 'pending' || docItem.status === 'requested' || docItem.status === 'rejected') && (
          <>
            <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleUpload} />
            {(() => {
              const useNewArch = studentId && subFolders?.documents;
              const btnBorder  = useNewArch ? '#7c3aed' : driveConfig ? (driveConfig.provider === 'google' ? '#16a34a' : '#2563eb') : '#7c3aed';
              const btnBg      = useNewArch ? 'rgba(124,58,237,0.08)' : driveConfig ? (driveConfig.provider === 'google' ? 'rgba(22,163,74,0.08)' : 'rgba(37,99,235,0.08)') : 'rgba(124,58,237,0.08)';
              const btnColor   = useNewArch ? '#7c3aed' : driveConfig ? (driveConfig.provider === 'google' ? '#16a34a' : '#2563eb') : '#7c3aed';
              const btnLabel   = useNewArch ? '📁 Upload' : driveConfig ? (driveConfig.provider === 'google' ? '🟢 To Drive' : '🔵 To OneDrive') : '⬆ Upload';
              return (
                <button onClick={() => { setDriveErr(''); fileRef.current?.click(); }} disabled={uploading} style={{
                  padding: '5px 10px', borderRadius: 7, border: `1px solid ${btnBorder}`,
                  background: btnBg, color: btnColor,
                  cursor: uploading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600,
                }}>{btnLabel}</button>
              );
            })()}
          </>
        )}
        {/* Verify / Reject (admin/counsellor) */}
        {docItem.status === 'uploaded' && (
          <>
            <button onClick={() => onUpdate(docItem.id, { status: 'verified', verifiedAt: serverTimestamp(), verifiedBy: currentUser?.id })} style={{
              padding: '5px 10px', borderRadius: 7, border: '1px solid #10b981',
              background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            }}>✓ Verify</button>
            <button onClick={async () => {
              const remark = window.prompt('Reason for rejection:');
              if (remark !== null) await onUpdate(docItem.id, { status: 'rejected', remark, rejectedAt: serverTimestamp() });
            }} style={{
              padding: '5px 10px', borderRadius: 7, border: '1px solid #ef4444',
              background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            }}>✗ Reject</button>
          </>
        )}
        {isAdmin && (
          <button onClick={handleDelete} style={{
            padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)',
            background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11,
          }}>🗑</button>
        )}
      </div>
    </div>
  );
}

// ─── Direct Upload Row (for standard doc types not yet in Firestore) ──────────
function DirectUploadRow({ docName, categoryId, subFolderType = 'other', companyId, leadId, currentUser, companyDriveConfig, driveConfig, studentProfile, selectedLead }) {
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [err,          setErr]          = useState('');
  const [uploadedFile, setUploadedFile] = useState(null); // { url, name } after success
  const fileRef = React.useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  const providerColor = companyDriveConfig?.provider === 'google' ? '#16a34a'
    : companyDriveConfig?.provider === 'microsoft' ? '#2563eb'
    : driveConfig?.provider === 'google' ? '#16a34a'
    : driveConfig?.provider === 'microsoft' ? '#2563eb'
    : '#7c3aed';

  const providerIcon = companyDriveConfig?.provider === 'google' ? '🟢'
    : companyDriveConfig?.provider === 'microsoft' ? '🔵'
    : driveConfig?.provider === 'google' ? '🟢'
    : driveConfig?.provider === 'microsoft' ? '🔵'
    : '⬆';

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !companyId || !leadId) return;
    setUploading(true);
    setErr('');

    // Build clean filename: use document type name + original extension
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    const cleanDocName = docName.replace(/[/\\?%*:|"<>]/g, '-').trim();
    const uploadFileName = ext ? `${cleanDocName}.${ext}` : cleanDocName;

    const useNewArch = !!companyDriveConfig;

    if (useNewArch || driveConfig) {
      try {
        const sName = selectedLead?.name ||
          `${selectedLead?.firstName || ''} ${selectedLead?.lastName || ''}`.trim() ||
          'Student';
        const counselorName = currentUser?.name || currentUser?.displayName || 'Counselor';

        const fd = new FormData();
        fd.append('file',        file);
        fd.append('companyId',   companyId);
        fd.append('userId',      currentUser?.id || '');
        fd.append('docName',     uploadFileName);  // clean doc-type name

        if (useNewArch) {
          fd.append('studentId',     leadId);
          fd.append('subFolderType', subFolderType);
          fd.append('studentName',   sName);
          fd.append('counselorName', counselorName);
        } else {
          fd.append('folderId',  driveConfig.folderId || '');
          fd.append('folderUrl', driveConfig.folderUrl || '');
          fd.append('provider',  driveConfig.provider);
        }

        const resp = await fetch(`${BACKEND_URL}/api/drive/upload`, { method: 'POST', body: fd });
        const data = await resp.json();
        if (!resp.ok || data.error) {
          if (data.needsReconnect || resp.status === 401) {
            throw new Error('__RECONNECT__');
          }
          throw new Error(data.error || 'Upload failed');
        }

        // Create Firestore doc entry
        const { db: fdb } = await import('../firebase/config');
        const { collection: col, addDoc, serverTimestamp: sts } = await import('firebase/firestore');
        await addDoc(col(fdb, 'companies', companyId, 'leads', leadId, 'documents'), {
          name:            docName,
          categoryId:      categoryId || null,
          status:          'uploaded',
          fileUrl:         data.fileUrl,
          fileId:          data.fileId,
          fileName:        uploadFileName,
          storageProvider: data.provider,
          storagePath:     null,
          companyId,   leadId,
          createdAt:   sts(), uploadedAt: sts(),
          uploadedBy:  currentUser?.id, requestedBy: currentUser?.id,
        });

        // Show inline success immediately (Firestore listener will replace this row shortly)
        setUploadedFile({ url: data.fileUrl, name: uploadFileName });
      } catch (err2) {
        setErr(err2.message);
      } finally {
        setUploading(false); setProgress(0);
        if (fileRef.current) fileRef.current.value = '';
      }
      return;
    }

    // Firebase Storage fallback
    try {
      const { storage } = await import('../firebase/config');
      const { ref: sref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
      const { db: fdb } = await import('../firebase/config');
      const { collection: col, addDoc, serverTimestamp: sts } = await import('firebase/firestore');
      const path = `documents/${companyId}/${leadId}/${Date.now()}_${uploadFileName}`;
      const storageRef = sref(storage, path);
      const task = uploadBytesResumable(storageRef, file);
      task.on('state_changed',
        snap => setProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        uploadErr => { setErr(uploadErr.message); setUploading(false); },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          await addDoc(col(fdb, 'companies', companyId, 'leads', leadId, 'documents'), {
            name: docName, categoryId: categoryId || null, status: 'uploaded',
            fileUrl: url, fileName: uploadFileName,
            storagePath: path, storageProvider: 'firebase',
            companyId, leadId,
            createdAt: sts(), uploadedAt: sts(), uploadedBy: currentUser?.id, requestedBy: currentUser?.id,
          });
          setUploadedFile({ url, name: uploadFileName });
          setUploading(false); setProgress(0);
          if (fileRef.current) fileRef.current.value = '';
        }
      );
    } catch (err3) {
      setErr(err3.message);
      setUploading(false);
    }
  };

  // After upload succeeds show a compact "uploaded" row until Firestore listener re-renders
  if (uploadedFile) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
        borderBottom: '1px solid var(--border)', background: 'rgba(16,185,129,0.04)',
      }}>
        <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{docName}</div>
          <div style={{ fontSize: 11, color: providerColor, marginTop: 2 }}>
            {providerIcon}{' '}
            <a href={uploadedFile.url} target="_blank" rel="noopener noreferrer"
              style={{ color: providerColor, textDecoration: 'underline' }}>
              {uploadedFile.name} ↗
            </a>
          </div>
        </div>
        <span style={{ fontSize: 11, color: '#10b981', padding: '2px 8px', borderRadius: 20, border: '1px solid #10b981', background: 'rgba(16,185,129,0.08)', fontWeight: 700 }}>Uploaded</span>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
      borderBottom: '1px solid var(--border)', transition: 'background 0.1s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0, opacity: 0.4 }}>📋</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>{docName}</div>
        {err === '__RECONNECT__'
          ? <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2 }}>
              ⚠️ Microsoft session expired —{' '}
              <a href="/settings" style={{ color: '#f59e0b', textDecoration: 'underline' }}>
                go to Settings → Email &amp; Calendar to reconnect
              </a>
            </div>
          : err && <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>⚠️ {err}</div>
        }
        {uploading && !err && (
          <div style={{ fontSize: 10, color: providerColor, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
            {progress > 0 ? `Uploading… ${progress}%` : 'Uploading to drive…'}
          </div>
        )}
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', padding: '2px 8px', borderRadius: 20, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>Not uploaded</span>
      <input type="file" ref={fileRef} style={{ display: 'none' }} onChange={handleUpload} />
      <button
        disabled={uploading}
        onClick={() => { setErr(''); fileRef.current?.click(); }}
        style={{
          padding: '5px 12px', borderRadius: 7, border: `1px solid ${providerColor}`,
          background: `${providerColor}15`, color: providerColor,
          cursor: uploading ? 'wait' : 'pointer', fontSize: 11, fontWeight: 600, flexShrink: 0,
          opacity: uploading ? 0.6 : 1,
        }}
      >{uploading ? '⏳' : `${providerIcon} Upload`}</button>
    </div>
  );
}

// ─── Eligibility Checker ──────────────────────────────────────────────────────
function EligibilityPanel({ documents, docCategories: cats }) {
  const resolvedCats = cats || DOC_CATEGORIES;
  const verified = documents.filter(d => d.status === 'verified').length;
  const total = documents.length;
  const pct = total ? Math.round(verified / total * 100) : 0;
  const color = pct === 100 ? '#10b981' : pct >= 60 ? '#f59e0b' : '#ef4444';

  const byCategory = resolvedCats.map(cat => {
    const catDocs = documents.filter(d => cat.docs.includes(d.name));
    const catVerified = catDocs.filter(d => d.status === 'verified').length;
    return { ...cat, total: catDocs.length, verified: catVerified };
  }).filter(c => c.total > 0);

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        <div style={{ position: 'relative', width: 72, height: 72 }}>
          <svg viewBox="0 0 36 36" style={{ width: 72, height: 72, transform: 'rotate(-90deg)' }}>
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--border)" strokeWidth="3" />
            <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
              strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color }}>
            {pct}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Document Completion</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            {verified} of {total} documents verified
          </div>
          {pct === 100 && <div style={{ fontSize: 12, color: '#10b981', fontWeight: 600, marginTop: 4 }}>🎉 All documents complete!</div>}
          {pct >= 60 && pct < 100 && <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, marginTop: 4 }}>⚡ Almost ready — {total - verified} remaining</div>}
          {pct < 60 && total > 0 && <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>⚠️ Documents incomplete</div>}
        </div>
      </div>

      {/* Category breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
        {byCategory.map(cat => (
          <div key={cat.id} style={{ padding: '8px 12px', borderRadius: 8, background: cat.verified === cat.total ? 'rgba(16,185,129,0.08)' : 'var(--bg-secondary)', border: `1px solid ${cat.verified === cat.total ? 'rgba(16,185,129,0.3)' : 'var(--border)'}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>{cat.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: cat.verified === cat.total ? '#10b981' : 'var(--text-primary)' }}>
              {cat.verified}/{cat.total}
              {cat.verified === cat.total && ' ✓'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Add Document Modal ───────────────────────────────────────────────────────
function AddDocModal({ leadId, companyId, onClose, onAdd, existingNames, docCategories: cats }) {
  const resolvedCats = cats || DOC_CATEGORIES;
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState('');

  const handleAdd = async () => {
    const names = [...selected, ...(custom.trim() ? [custom.trim()] : [])];
    for (const name of names) {
      await onAdd({ name, status: 'pending', leadId, companyId });
    }
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-primary)', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Add Custom Document</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {resolvedCats.map(cat => (
            <div key={cat.id} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>{cat.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cat.docs.map(name => {
                  const already = existingNames.includes(name);
                  const isSelected = selected.includes(name);
                  return (
                    <button key={name} disabled={already} onClick={() => setSelected(s =>
                      s.includes(name) ? s.filter(x => x !== name) : [...s, name]
                    )} style={{
                      padding: '5px 10px', borderRadius: 20, fontSize: 11, cursor: already ? 'not-allowed' : 'pointer',
                      border: isSelected ? '2px solid #7c3aed' : '1px solid var(--border)',
                      background: already ? 'var(--border)' : isSelected ? 'rgba(124,58,237,0.1)' : 'transparent',
                      color: already ? 'var(--text-secondary)' : isSelected ? '#7c3aed' : 'var(--text-primary)',
                      fontWeight: 600, opacity: already ? 0.5 : 1,
                    }}>{already ? '✓ ' : ''}{name}</button>
                  );
                })}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Custom document name</label>
            <input className="crm-input" value={custom} onChange={e => setCustom(e.target.value)}
              placeholder="e.g. Research paper abstract" style={{ width: '100%', marginTop: 4 }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{selected.length} selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 10, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
            <button onClick={handleAdd} disabled={!selected.length && !custom.trim()} className="btn-primary" style={{ padding: '9px 22px', opacity: (!selected.length && !custom.trim()) ? 0.5 : 1 }}>
              Add {selected.length > 0 ? `(${selected.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Status Update Timeline ───────────────────────────────────────────────────
function StatusUpdates({ updates, onAdd, currentUser }) {
  const [text, setText] = useState('');
  const [type, setType] = useState('update');

  const TYPES = {
    update:  { icon: '📢', color: '#0ea5e9', label: 'Update' },
    note:    { icon: '📝', color: '#7c3aed', label: 'Note'   },
    call:    { icon: '📞', color: '#10b981', label: 'Call'   },
    email:   { icon: '📧', color: '#f59e0b', label: 'Email'  },
    meeting: { icon: '🤝', color: '#8b5cf6', label: 'Meeting'},
  };

  const handleAdd = async () => {
    if (!text.trim()) return;
    await onAdd({ text: text.trim(), type, createdBy: currentUser?.id, createdByName: currentUser?.name, createdAt: serverTimestamp() });
    setText('');
  };

  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>📋 Status Updates</div>

      {/* Add update */}
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          {Object.entries(TYPES).map(([k, v]) => (
            <button key={k} onClick={() => setType(k)} style={{
              padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
              border: type === k ? `2px solid ${v.color}` : '1px solid var(--border)',
              background: type === k ? v.color + '18' : 'transparent',
              color: type === k ? v.color : 'var(--text-secondary)', fontWeight: 600,
            }}>{v.icon} {v.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="crm-input" value={text} onChange={e => setText(e.target.value)}
            placeholder="Add a status update, note, or next step…" style={{ flex: 1 }}
            onKeyDown={e => e.key === 'Enter' && handleAdd()} />
          <button onClick={handleAdd} className="btn-primary" style={{ padding: '8px 16px', fontSize: 13, flexShrink: 0 }}>Post</button>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative' }}>
        {updates.map((u, i) => {
          const t = TYPES[u.type] || TYPES.update;
          return (
            <div key={u.id || i} style={{ display: 'flex', gap: 10, marginBottom: 12, position: 'relative' }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', background: t.color + '18',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, flexShrink: 0, border: `1px solid ${t.color}40`,
              }}>{t.icon}</div>
              <div style={{ flex: 1, paddingTop: 4 }}>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{u.text}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                  <span style={{ color: t.color, fontWeight: 600 }}>{t.label}</span>
                  {' · '}{u.createdByName || 'Team'}
                  {u.createdAt?.toDate && ' · ' + u.createdAt.toDate().toLocaleDateString()}
                </div>
              </div>
            </div>
          );
        })}
        {updates.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
            No updates yet — add the first one above
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Documents Page ──────────────────────────────────────────────────────
export default function DocumentsPage() {
  const { currentUser, company, leads } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const companyId = currentUser?.companyId;

  // ── Dynamic document types (admin-configurable from Settings → Doc Types) ──
  const [docCategories, setDocCategories] = useState(DOC_CATEGORIES);
  useEffect(() => {
    if (!companyId) return;
    let unsub;
    Promise.all([import('firebase/firestore'), import('../firebase/config')]).then(([ff, fc]) => {
      unsub = ff.onSnapshot(
        ff.doc(fc.db, 'companies', companyId, 'settings', 'documentTypes'),
        snap => {
          if (snap.exists() && Array.isArray(snap.data()?.categories) && snap.data().categories.length) {
            setDocCategories(snap.data().categories);
          } else {
            setDocCategories(DOC_CATEGORIES);
          }
        },
        () => setDocCategories(DOC_CATEGORIES)
      );
    });
    return () => unsub && unsub();
  }, [companyId]);

  const allDocNames = useMemo(() => docCategories.flatMap(c => c.docs), [docCategories]);

  const [selectedLead, setSelectedLead] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [addDocModal, setAddDocModal] = useState(false);
  const [searchLead, setSearchLead] = useState('');
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [leadDocStats, setLeadDocStats] = useState({});
  const [page, setPage] = useState(0);

  // Connected drive account for current counselor
  const [connectedDrive, setConnectedDrive] = useState(null); // 'google' | 'microsoft' | null

  // Company-level shared drive config (new arch)
  const [companyDriveConfig, setCompanyDriveConfig] = useState(null);
  const [ensuringFolders,    setEnsuringFolders]    = useState(false);

  // Cloud folder (from studentProfiles subcollection)
  const [studentProfile,      setStudentProfile]      = useState(null);
  const [studentProfileLoaded, setStudentProfileLoaded] = useState(false);
  const [folderUrlEdit,       setFolderUrlEdit]       = useState('');
  const [editingFolder,       setEditingFolder]       = useState(false);
  const [savingFolder,        setSavingFolder]        = useState(false);
  const [creatingFolder,      setCreatingFolder]      = useState(false);
  const PAGE_SIZE = 20;

  // FB-lead-aware name helper
  const getLeadName = (lead) => {
    if (!lead) return '—';
    const first = lead.firstName || '';
    const last  = lead.lastName  || '';
    if (first || last) return (first + ' ' + last).trim();
    return lead.name || lead.email || lead.phone || '—';
  };

  // Filter leads by counsellor if needed
  const visibleLeads = useMemo(() => {
    if (!leads) return [];
    if (isAdmin) return leads;
    return leads.filter(l => l.assignedTo === currentUser?.id || l.assignedToId === currentUser?.id || l.assignedTo === currentUser?.name);
  }, [leads, isAdmin, currentUser]);

  // Only show leads that have documents by default
  const leadsWithDocs = useMemo(() => {
    return visibleLeads.filter(l => (leadDocStats[l.id]?.total || 0) > 0);
  }, [visibleLeads, leadDocStats]);

  const filteredLeads = useMemo(() => {
    const base = showAllLeads ? visibleLeads : leadsWithDocs;
    const named = base.filter(l => getLeadName(l) !== '—');
    if (!searchLead) return named;
    const q = searchLead.toLowerCase();
    return named.filter(l => getLeadName(l).toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q));
  }, [visibleLeads, leadsWithDocs, showAllLeads, searchLead]); // eslint-disable-line

  // Reset page when filter changes
  useEffect(() => { setPage(0); }, [searchLead, showAllLeads]);

  // Load company-level shared drive config (admin-configured, new architecture)
  useEffect(() => {
    if (!company?.id) return;
    fetch(`${BACKEND_URL}/api/drive/company-config?companyId=${company.id}`)
      .then(r => r.json())
      .then(data => { if (!data.error && data.provider) setCompanyDriveConfig(data); })
      .catch(() => {});
  }, [company?.id]); // eslint-disable-line

  const totalPages = Math.ceil(filteredLeads.length / PAGE_SIZE);
  const pagedLeads = filteredLeads.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Load documents for selected lead
  useEffect(() => {
    if (!selectedLead || !company?.id) { setDocuments([]); return; }
    setLoadingDocs(true);
    const unsub = onSnapshot(
      collection(db, 'companies', company.id, 'leads', selectedLead.id, 'documents'),
      snap => {
        setDocuments(snap.docs.map(d => ({ ...d.data(), id: d.id })));
        setLoadingDocs(false);
      },
      () => setLoadingDocs(false)
    );
    return unsub;
  }, [selectedLead?.id, company?.id]);

  // Load status updates for selected lead
  useEffect(() => {
    if (!selectedLead || !company?.id) { setUpdates([]); return; }
    const unsub = onSnapshot(
      collection(db, 'companies', company.id, 'leads', selectedLead.id, 'statusUpdates'),
      snap => {
        const items = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        items.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setUpdates(items);
      }
    );
    return unsub;
  }, [selectedLead?.id, company?.id]);

  // Check if current counselor has a connected drive account via backend status API
  // (connectedAccounts is locked client-side; Admin SDK on backend reads it safely)
  useEffect(() => {
    if (!currentUser?.id || !company?.id) return;
    let cancelled = false;
    // Use currentUser.id — the Firestore doc ID (= Firebase UID).
    // currentUser.uid does NOT exist as a Firestore field.
    fetch(`${BACKEND_URL}/api/oauth/status?companyId=${company.id}&userId=${currentUser.id}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        // Use whichever account is connected in 'work' mode
        // (authMode is returned by the status endpoint — defaults to 'work' for pre-existing accounts)
        const workProvider = Object.entries(data).find(([, v]) => v.connected && v.authMode === 'work')?.[0] || null;
        // Fallback: if no work-mode account, use any connected account
        const anyProvider  = workProvider || Object.entries(data).find(([, v]) => v.connected)?.[0] || null;
        setConnectedDrive(anyProvider);
      })
      .catch(() => { if (!cancelled) setConnectedDrive(null); });
    return () => { cancelled = true; };
  }, [currentUser?.id, company?.id]); // eslint-disable-line

  // Subscribe to studentProfile for cloud folder URL (and for subfolder IDs in new arch)
  useEffect(() => {
    if (!selectedLead || !company?.id) {
      setStudentProfile(null);
      setStudentProfileLoaded(false);
      setFolderUrlEdit('');
      return;
    }
    setStudentProfileLoaded(false);
    const ref = doc(db, 'companies', company.id, 'studentProfiles', selectedLead.id);
    return onSnapshot(ref, snap => {
      const data = snap.exists() ? snap.data() : null;
      setStudentProfile(data);
      setStudentProfileLoaded(true);
      setFolderUrlEdit(data?.documentFolderUrl || '');
      setEditingFolder(false);
    });
  }, [selectedLead?.id, company?.id]); // eslint-disable-line

  // Auto-create per-student folder structure when company drive config is set (new architecture)
  // Fires once per student selection when folders haven't been created yet
  useEffect(() => {
    if (!companyDriveConfig || !selectedLead || !company?.id) return;
    if (!studentProfileLoaded) return; // wait for profile snapshot
    if (ensuringFolders) return;
    if (studentProfile?.documentFolderId) return; // folders already exist
    const counselorName = currentUser?.name || 'Counselor';
    const studentName = getLeadName(selectedLead);
    if (!studentName || studentName === '—') return;
    setEnsuringFolders(true);
    fetch(`${BACKEND_URL}/api/drive/ensure-student-folders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId: company.id, studentId: selectedLead.id, counselorName, studentName }),
    })
      .then(r => r.json())
      .then(data => { if (data.error) console.warn('[Documents] ensure-student-folders:', data.error); })
      .catch(err => console.warn('[Documents] ensure-student-folders error:', err.message))
      .finally(() => setEnsuringFolders(false));
  }, [selectedLead?.id, studentProfileLoaded, studentProfile?.documentFolderId, companyDriveConfig]); // eslint-disable-line

  const saveFolderUrl = async () => {
    if (!company?.id || !selectedLead) return;
    setSavingFolder(true);
    try {
      await setDoc(
        doc(db, 'companies', company.id, 'studentProfiles', selectedLead.id),
        { documentFolderUrl: folderUrlEdit.trim(), updatedAt: serverTimestamp() },
        { merge: true }
      );
      setEditingFolder(false);
    } finally {
      setSavingFolder(false);
    }
  };

  // Create a new folder in Google Drive / OneDrive directly from the CRM
  const handleCreateFolder = async () => {
    if (!connectedDrive || !selectedLead) return;
    const leadName = getLeadName(selectedLead);
    const folderName = `${leadName} — CAE Documents`;
    setCreatingFolder(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/drive/create-folder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company.id,
          userId:    currentUser?.id,
          provider:  connectedDrive,
          folderName,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Folder creation failed');
      // Save the returned folder URL to Firestore and update local state
      const newUrl = data.folderUrl;
      await setDoc(
        doc(db, 'companies', company.id, 'studentProfiles', selectedLead.id),
        { documentFolderUrl: newUrl, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setFolderUrlEdit(newUrl);
    } catch (err) {
      alert('Could not create folder: ' + err.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleAddDocs = async (docData) => {
    await addDoc(collection(db, 'companies', company.id, 'leads', selectedLead.id, 'documents'), {
      ...docData, createdAt: serverTimestamp(), requestedBy: currentUser?.id,
    });
  };

  const handleUpdateDoc = async (docId, data) => {
    await updateDoc(doc(db, 'companies', company.id, 'leads', selectedLead.id, 'documents', docId), data);
  };

  const handleDeleteDoc = async (docId) => {
    await deleteDoc(doc(db, 'companies', company.id, 'leads', selectedLead.id, 'documents', docId));
  };

  const handleAddUpdate = async (updateData) => {
    await addDoc(collection(db, 'companies', company.id, 'leads', selectedLead.id, 'statusUpdates'), updateData);
  };

  // Compute per-lead doc stats for the sidebar list
  useEffect(() => {
    if (!company?.id || !visibleLeads.length) return;
    // Listen to a few leads' doc counts (lightweight)
    const unsubs = visibleLeads.slice(0, 30).map(lead => {
      return onSnapshot(
        collection(db, 'companies', company.id, 'leads', lead.id, 'documents'),
        snap => {
          const docs = snap.docs.map(d => d.data());
          const total = docs.length;
          const verified = docs.filter(d => d.status === 'verified').length;
          const pending = docs.filter(d => d.status === 'pending' || d.status === 'uploaded').length;
          setLeadDocStats(s => ({ ...s, [lead.id]: { total, verified, pending } }));
        }
      );
    });
    return () => unsubs.forEach(u => u());
  }, [visibleLeads.length, company?.id]); // eslint-disable-line

  const docsByCategory = useMemo(() => {
    return docCategories.map(cat => ({
      ...cat,
      items: documents.filter(d => cat.docs.includes(d.name)),
    })).filter(c => c.items.length > 0);
  }, [documents, docCategories]);

  const uncategorised = documents.filter(d => !allDocNames.includes(d.name));

  // Derive Drive config from the linked folder URL for this student
  const driveConfig = useMemo(
    () => parseFolderUrl(studentProfile?.documentFolderUrl),
    [studentProfile?.documentFolderUrl]
  );

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
      {/* ── Left: Lead List ─────────────────────────────────────────────── */}
      <div style={{ width: 280, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ padding: '18px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>📁 Documents</h2>
            <button onClick={() => setShowAllLeads(s => !s)} style={{
              fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
              border: '1px solid var(--border)', background: showAllLeads ? 'rgba(124,58,237,0.1)' : 'transparent',
              color: showAllLeads ? '#7c3aed' : 'var(--text-secondary)', cursor: 'pointer',
            }}>{showAllLeads ? 'Active only' : `All (${visibleLeads.length})`}</button>
          </div>

          {/* Drive connection status — prefer company config, fall back to personal */}
          {(() => {
            const prov  = companyDriveConfig?.provider || connectedDrive;
            const isMs  = prov === 'microsoft';
            if (prov) return (
              <div style={{ background: isMs ? '#eff6ff' : '#f0fdf4', border: `1px solid ${isMs ? '#93c5fd' : '#86efac'}`, borderRadius: 8, padding: '7px 10px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 14 }}>{isMs ? '🔵' : '🟢'}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: isMs ? '#1d4ed8' : '#15803d' }}>
                    {isMs ? 'OneDrive Connected' : 'Google Drive Connected'}
                  </div>
                  <div style={{ fontSize: 10, color: isMs ? '#1e40af' : '#166534' }}>
                    {companyDriveConfig ? 'Shared team folder · folders auto-created per student' : 'Files save to your work account drive'}
                  </div>
                </div>
              </div>
            );
            return (
              <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '7px 10px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 14 }}>⚠️</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>No drive configured</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Admin: set up shared folder in Settings → Documents Drive</div>
                </div>
              </div>
            );
          })()}

          <input className="crm-input" placeholder="Search leads…" value={searchLead}
            onChange={e => setSearchLead(e.target.value)} style={{ width: '100%', fontSize: 12 }} />
          {!showAllLeads && (
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 6 }}>
              Showing {leadsWithDocs.length} active · <button onClick={() => setShowAllLeads(true)} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 10, fontWeight: 600, padding: 0 }}>Show all</button>
            </div>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {pagedLeads.map(lead => {
            const stats = leadDocStats[lead.id] || { total: 0, verified: 0, pending: 0 };
            const active = selectedLead?.id === lead.id;
            return (
              <div key={lead.id} onClick={() => setSelectedLead(lead)} style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                background: active ? 'rgba(124,58,237,0.07)' : 'transparent',
                borderLeft: active ? '3px solid #7c3aed' : '3px solid transparent',
                transition: 'all 0.1s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>{(getLeadName(lead) || '?')[0].toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getLeadName(lead)}</div>
                    {stats.total > 0 ? (
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                        ✅ {stats.verified}/{stats.total}
                        {stats.pending > 0 && <span style={{ color: '#f59e0b', marginLeft: 6 }}>⏳ {stats.pending} pending</span>}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>Click to add documents</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {filteredLeads.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
                {showAllLeads ? 'No leads found' : 'No active documents'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
                {showAllLeads ? 'Try a different search' : 'Start collecting docs for a student'}
              </div>
              {!showAllLeads && (
                <button onClick={() => setShowAllLeads(true)} style={{
                  fontSize: 11, padding: '6px 14px', borderRadius: 8, border: '1px solid #7c3aed',
                  background: 'rgba(124,58,237,0.08)', color: '#7c3aed', cursor: 'pointer', fontWeight: 600,
                }}>Browse all leads</button>
              )}
            </div>
          )}
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{
              padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'none',
              cursor: page === 0 ? 'not-allowed' : 'pointer', fontSize: 12, color: page === 0 ? 'var(--text-secondary)' : 'var(--text-primary)',
            }}>← Prev</button>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {page + 1} / {totalPages} &nbsp;·&nbsp; {filteredLeads.length} leads
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{
              padding: '5px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'none',
              cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', fontSize: 12, color: page >= totalPages - 1 ? 'var(--text-secondary)' : 'var(--text-primary)',
            }}>Next →</button>
          </div>
        )}
      </div>

      {/* ── Right: Document Panel ────────────────────────────────────────── */}
      {!selectedLead ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
          <span style={{ fontSize: 48 }}>📂</span>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Select a lead</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Choose a lead from the left to manage their documents</div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <div>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{getLeadName(selectedLead)}</h2>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {selectedLead.email} {selectedLead.phone && '· ' + selectedLead.phone}
              </div>
            </div>
            <button onClick={() => setAddDocModal(true)} className="btn-primary" style={{ fontSize: 13 }}>
              + Add Custom Document
            </button>
          </div>

          {/* ── Cloud Document Folder ──────────────────────────────────────── */}
          {companyDriveConfig ? (
            /* ── NEW ARCH: Company shared drive, auto per-student folders ─── */
            (() => {
              const provider   = companyDriveConfig.provider;
              const driveIcon  = provider === 'google' ? '🟢' : '🔵';
              const driveLabel = provider === 'google' ? 'Google Drive' : 'OneDrive';
              const folderUrl  = studentProfile?.documentFolderUrl || studentProfile?.studentFolderUrl;
              return (
                <div style={{ marginBottom: 18, borderRadius: 12, border: `1px solid ${folderUrl ? '#3b82f620' : 'var(--border)'}`, background: folderUrl ? 'linear-gradient(135deg,#eff6ff,#f0fdf4)' : 'var(--bg-card)', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{folderUrl ? driveIcon : ensuringFolders ? '⏳' : '📂'}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {ensuringFolders ? 'Creating folder structure…' : folderUrl ? `${driveLabel} — Student Folder` : `${driveLabel} folder will be created automatically`}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                          {ensuringFolders
                            ? `Setting up Essays, LORs, Documents & more in the shared ${driveLabel}…`
                            : folderUrl
                              ? `${driveIcon} Uploads auto-route to the correct subfolder — no manual linking needed`
                              : `Shared folder is configured in Settings → Documents Drive`}
                        </div>
                      </div>
                    </div>
                    {folderUrl && !ensuringFolders && (
                      <a href={folderUrl} target="_blank" rel="noopener noreferrer"
                        style={{ padding: '6px 14px', borderRadius: 8, background: provider === 'google' ? '#16a34a' : '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        Open Folder ↗
                      </a>
                    )}
                    {ensuringFolders && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Please wait…</div>
                    )}
                  </div>
                  {/* Subfolder preview when ready */}
                  {studentProfile?.subFolders && !ensuringFolders && (
                    <div style={{ padding: '0 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {Object.entries(studentProfile.subFolders).map(([key, sf]) => (
                        sf?.url ? (
                          <a key={key} href={sf.url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 20, border: `1px solid ${provider === 'google' ? '#86efac' : '#93c5fd'}`, color: provider === 'google' ? '#166534' : '#1e40af', background: provider === 'google' ? '#f0fdf4' : '#eff6ff', textDecoration: 'none', fontWeight: 600, textTransform: 'capitalize' }}>
                            📁 {key === 'lors' ? 'LORs' : key.charAt(0).toUpperCase() + key.slice(1)} ↗
                          </a>
                        ) : null
                      ))}
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            /* ── LEGACY ARCH: per-student manual folder linking ──────────── */
            (() => {
              const url = studentProfile?.documentFolderUrl;
              const isDrive    = url?.includes('drive.google.com');
              const isOneDrive = url?.includes('onedrive.live.com') || url?.includes('sharepoint.com') || url?.includes('1drv.ms');
              const driveIcon  = isDrive ? '🟢' : isOneDrive ? '🔵' : '☁️';
              const driveLabel = isDrive ? 'Google Drive' : isOneDrive ? 'OneDrive' : 'Cloud Folder';

              return (
                <div style={{ marginBottom: 18, borderRadius: 12, border: `1px solid ${url ? '#3b82f620' : 'var(--border)'}`, background: url ? 'linear-gradient(135deg,#eff6ff,#f0fdf4)' : 'var(--bg-card)', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{url ? driveIcon : '📂'}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {url ? `${driveLabel} Folder` : 'Cloud Document Folder'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                          {url ? (isDrive ? '🟢 Uploads go directly to Google Drive' : isOneDrive ? '🔵 Uploads go directly to OneDrive' : 'Student documents are stored in your work drive') : 'Link this student\'s OneDrive or Google Drive folder'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      {url && !editingFolder && (
                        <>
                          <a href={url} target="_blank" rel="noopener noreferrer"
                            style={{ padding: '6px 14px', borderRadius: 8, background: isDrive ? '#16a34a' : isOneDrive ? '#2563eb' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            Open Folder ↗
                          </a>
                          <button onClick={() => { setFolderUrlEdit(url); setEditingFolder(true); }}
                            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer' }}>
                            ✏️ Change
                          </button>
                        </>
                      )}
                      {!url && !editingFolder && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          {connectedDrive && (
                            <button onClick={handleCreateFolder} disabled={creatingFolder}
                              style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: connectedDrive === 'microsoft' ? '#2563eb' : '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: creatingFolder ? 'wait' : 'pointer', opacity: creatingFolder ? 0.7 : 1, whiteSpace: 'nowrap' }}>
                              {creatingFolder ? '⏳ Creating…' : `✨ Create ${connectedDrive === 'microsoft' ? 'OneDrive' : 'Drive'} Folder`}
                            </button>
                          )}
                          <button onClick={() => setEditingFolder(true)}
                            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid #7c3aed', background: 'rgba(124,58,237,0.08)', color: '#7c3aed', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                            🔗 Link Existing
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {editingFolder && (
                    <div style={{ padding: '12px 16px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input className="crm-input" value={folderUrlEdit}
                        onChange={e => setFolderUrlEdit(e.target.value)}
                        placeholder="https://drive.google.com/drive/folders/… or OneDrive link"
                        style={{ flex: 1, fontSize: 12 }}
                        onKeyDown={e => e.key === 'Enter' && saveFolderUrl()} />
                      <button onClick={saveFolderUrl} disabled={savingFolder}
                        style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12, cursor: savingFolder ? 'wait' : 'pointer', whiteSpace: 'nowrap', opacity: savingFolder ? 0.7 : 1 }}>
                        {savingFolder ? 'Saving…' : '💾 Save'}
                      </button>
                      <button onClick={() => { setFolderUrlEdit(url || ''); setEditingFolder(false); }}
                        style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>
                        Cancel
                      </button>
                    </div>
                  )}
                  {!url && !editingFolder && (
                    <div style={{ padding: '0 16px 12px', fontSize: 11, color: 'var(--text-muted)' }}>
                      {connectedDrive
                        ? `✨ Click "Create ${connectedDrive === 'microsoft' ? 'OneDrive' : 'Drive'} Folder" to auto-create a folder for this student, or link an existing one.`
                        : '💡 Connect your drive account in Meetings tab, then create or link a folder for this student here.'}
                    </div>
                  )}
                </div>
              );
            })()
          )}

          {/* Eligibility */}
          {documents.length > 0 && <EligibilityPanel documents={documents} docCategories={docCategories} />}

          {/* Documents — always show all categories with upload buttons */}
          {loadingDocs ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>
          ) : (
            <>
              {docCategories.map(cat => {
                // Firestore items in this category
                const catItems = documents.filter(d => {
                  const catDef = docCategories.find(c => c.id === d.categoryId);
                  if (catDef) return catDef.id === cat.id;
                  return cat.docs.includes(d.name);
                });
                // Names already in Firestore for this category
                const uploadedNames = new Set(catItems.map(d => d.name));
                // Remaining standard doc names not yet uploaded
                const pendingNames = cat.docs.filter(n => !uploadedNames.has(n));
                return (
                  <div key={cat.id} style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 14, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{cat.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        {catItems.filter(d => d.status === 'verified' || d.fileUrl).length}/{cat.docs.length} uploaded
                      </span>
                    </div>
                    {/* Existing Firestore document rows */}
                    {catItems.map(docItem => (
                      <DocumentRow key={docItem.id} docItem={docItem}
                        onUpdate={handleUpdateDoc} onDelete={handleDeleteDoc}
                        isAdmin={isAdmin} currentUser={currentUser}
                        driveConfig={companyDriveConfig ? null : driveConfig}
                        studentId={companyDriveConfig ? selectedLead.id : null}
                        subFolders={companyDriveConfig ? studentProfile?.subFolders : null} />
                    ))}
                    {/* Standard doc names not yet in Firestore — direct upload rows */}
                    {pendingNames.map(docName => (
                      <DirectUploadRow key={docName} docName={docName}
                        categoryId={cat.id}
                        subFolderType={CATEGORY_SUBFOLDER[cat.id] || 'other'}
                        companyId={company?.id} leadId={selectedLead?.id}
                        currentUser={currentUser}
                        companyDriveConfig={companyDriveConfig}
                        driveConfig={driveConfig}
                        studentProfile={studentProfile}
                        selectedLead={selectedLead} />
                    ))}
                  </div>
                );
              })}
              {uncategorised.length > 0 && (
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>📁 Other</span>
                  </div>
                  {uncategorised.map(docItem => (
                    <DocumentRow key={docItem.id} docItem={docItem}
                      onUpdate={handleUpdateDoc} onDelete={handleDeleteDoc}
                      isAdmin={isAdmin} currentUser={currentUser}
                      driveConfig={companyDriveConfig ? null : driveConfig}
                      studentId={companyDriveConfig ? selectedLead.id : null}
                      subFolders={companyDriveConfig ? studentProfile?.subFolders : null} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Status Updates */}
          <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, marginTop: 6 }}>
            <StatusUpdates updates={updates} onAdd={handleAddUpdate} currentUser={currentUser} />
          </div>
        </div>
      )}

      {/* Add Doc Modal */}
      {addDocModal && selectedLead && (
        <AddDocModal
          leadId={selectedLead.id} companyId={company.id}
          existingNames={documents.map(d => d.name)}
          onClose={() => setAddDocModal(false)}
          onAdd={handleAddDocs}
          docCategories={docCategories}
        />
      )}
    </div>
  );
}
